// SAM-624 — how a stored booking line (booking.booking_options, pricing.py output) or a receipt
// line reads in the detail panel once pricing v2 is on:
//
//   - `line_type: "unit"` (SAM-591) is the cabana/unit base charge, NOT a guest: it carries
//     total_guest 0, so it is labelled Cabana / Unit and shown with quantity 1.
//   - `rate_group` (SAM-588), `event_size` (SAM-592) and a season label (SAM-589) are shown under
//     the line's name when the stored line carries them.
//
// Pure, no React — see bookingLineDisplay.test.ts. Every field is optional: bookings written
// before pricing v2 have none of them and render exactly as before.

import { EVENT_SIZE_BANDS } from '../../utils/eventSize';

export interface BookingLineLike {
  line_type?: string | null;
  type?: string | null;
  label_en?: string | null;
  label_es?: string | null;
  label?: string | { en?: string | null; es?: string | null } | null;
  option_id?: string | null;
  short_desc_en?: string | null;
  total_guest?: number | null;
  quantity?: number | null;
  resource_key?: string | null;
  resource_name?: string | null;
  included_pax?: number | null;
  max_pax?: number | null;
  rate_group?: string | null;
  event_size?: string | null;
  season?: string | { id?: string; label?: string | { en?: string | null; es?: string | null } | null } | null;
  season_label?: string | { en?: string | null; es?: string | null } | null;
  price?: number | null;
  unit_price?: number | null;
  total_amount?: number | null;
  subtotal?: number | null;
  /** pricing.py stores the units not charged (unit `included_pax`, tier-group `included`) here. */
  tier_group_free_qty?: number | null;
}

/** Lookups from the booking's service (settings.resources, rate_groups); all optional. */
export interface BookingLineContext {
  rateGroupLabel?: (id: string) => string | null | undefined;
  resourceName?: (key: string) => string | null | undefined;
}

export type BookingLineKind = 'guest' | 'unit' | 'add_on';

export interface BookingLineDisplay {
  kind: BookingLineKind;
  title: string;
  /** Extra lines under the title (rate group, event size, season, unit allowance). */
  details: string[];
  /** Quantity column: the unit charge is one unit even though it seats no guests. */
  quantity: number | null;
  /** Quantity column text; "5 (4 incl.)" when some units are not charged, so qty x unit = subtotal. */
  quantityText: string;
  includedQuantity: number;
}

const text = (v: unknown): string | null => {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (v && typeof v === 'object') {
    const o = v as { en?: string | null; es?: string | null };
    return o.en || o.es || null;
  }
  return null;
};

export function bookingLineKind(line: BookingLineLike): BookingLineKind {
  const t = line.line_type || line.type;
  return t === 'unit' ? 'unit' : t === 'add_on' ? 'add_on' : 'guest';
}

export function seasonLabelOf(line: BookingLineLike): string | null {
  const s = line.season;
  if (s && typeof s === 'object') return text(s.label) || (typeof s.id === 'string' ? s.id : null);
  return text(line.season_label) || text(s);
}

export function eventSizeText(size: string | null | undefined): string | null {
  if (!size) return null;
  const band = EVENT_SIZE_BANDS.find((b) => b.key === size);
  return band ? `Event size ${band.key.toUpperCase()} (${band.label})` : `Event size ${String(size).toUpperCase()}`;
}

/**
 * Units of a guest line that were not charged. Prefers the stored `tier_group_free_qty`; a receipt
 * line does not carry it, so it is derived from the amounts: extras = subtotal / unit price. With
 * a $0 unit price nothing can be derived (and nothing contradicts qty x unit = subtotal), so 0.
 */
export function includedQuantityOf(line: BookingLineLike, quantity: number | null): number {
  if (quantity == null || quantity <= 0) return 0;
  const stored = line.tier_group_free_qty;
  if (typeof stored === 'number' && stored > 0) return Math.min(stored, quantity);
  const price = Number(line.price ?? line.unit_price ?? 0);
  const total = line.total_amount ?? line.subtotal;
  if (!(price > 0) || total == null) return 0;
  const extras = Math.round(Number(total) / price);
  return extras >= 0 && extras < quantity ? quantity - extras : 0;
}

export function describeBookingLine(line: BookingLineLike, index = 0, ctx: BookingLineContext = {}): BookingLineDisplay {
  const kind = bookingLineKind(line);
  const details: string[] = [];

  let title = text(line.label_en) || text(line.label) || text(line.label_es) || line.option_id || `Item ${index + 1}`;
  let quantity: number | null = line.total_guest ?? line.quantity ?? null;

  if (kind === 'unit') {
    const resource = line.resource_name || (line.resource_key ? ctx.resourceName?.(line.resource_key) || line.resource_key : null);
    title = resource ? `Cabana / Unit — ${resource}` : 'Cabana / Unit';
    quantity = 1;
    if (line.included_pax != null) {
      details.push(`Includes ${line.included_pax} ${line.included_pax === 1 ? 'guest' : 'guests'}${line.max_pax ? `, up to ${line.max_pax}` : ''}`);
    }
  }
  if (line.rate_group) details.push(`Rate group: ${ctx.rateGroupLabel?.(line.rate_group) || line.rate_group}`);
  const size = eventSizeText(line.event_size);
  if (size) details.push(size);
  const season = seasonLabelOf(line);
  if (season) details.push(`Season: ${season}`);

  const includedQuantity = kind === 'guest' ? includedQuantityOf(line, quantity) : 0;
  if (includedQuantity > 0 && quantity != null) {
    const extra = quantity - includedQuantity;
    const price = Number(line.price ?? line.unit_price ?? 0);
    details.push(`${includedQuantity} included · ${extra} extra${extra > 0 && price > 0 ? ` × $${price.toFixed(2)}` : ''}`);
  }
  const quantityText = includedQuantity > 0 ? `${quantity} (${includedQuantity} incl.)` : quantity == null ? '—' : String(quantity);
  return { kind, title, details, quantity, quantityText, includedQuantity };
}
